import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100vh;
  padding: 24px;
  font-family: 'Sora', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #f8f9fa;
  color: #202124;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const Message = styled.p`
  font-size: 13px;
  color: #5f6368;
  margin: 0;
  max-width: 480px;
`;

const Details = styled.pre`
  font-size: 11px;
  color: #5f6368;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 4px;
  padding: 10px 12px;
  max-width: 640px;
  max-height: 180px;
  overflow: auto;
  text-align: left;
  white-space: pre-wrap;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: #1a73e8;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #1765cc;
  }
`;

const SecondaryButton = styled(Button)`
  background: #ffffff;
  color: #5f6368;
  border: 1px solid #dadce0;

  &:hover {
    background: #f1f3f4;
  }
`;

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Keeps a render error from tearing down the whole page. Without this, one bad
 * value anywhere in the tree leaves the user staring at a blank white screen
 * with no way back other than guessing that a reload might help.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    try {
      localStorage.removeItem('circuit-schema');
    } catch {
      // Ignore — storage may be unavailable.
    }
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Container>
        <Title>Something went wrong</Title>
        <Message>
          The editor hit an unexpected error. Your circuit is still saved locally —
          reloading usually recovers it. If the error keeps coming back, discard the
          saved circuit and start fresh.
        </Message>
        <Details>{error.message}</Details>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={this.handleReload}>Reload</Button>
          <SecondaryButton onClick={this.handleReset}>
            Discard saved circuit and reload
          </SecondaryButton>
        </div>
      </Container>
    );
  }
}
